import { useState, useMemo, useCallback } from "react";
import {
  AlertTriangle,
  CheckCircle,
  Download,
  Info,
  Loader2,
  XCircle,
} from "lucide-react";
import { db, type PaletteRecord, type PaletteAuditMetadata } from "../../lib/database";
import { accessibilityTools, type TargetCompliance } from "../../lib/accessibilityTools";
import type { PaletteAuditReport, PaletteAuditPairResult, PaletteAutoFixProposal } from "../../lib/accessibilityTools";
import { ConfusableOverlay, type ConfusablePair } from "./ConfusableOverlay";

const describeConfusableSeverity = (
  deltaE: number,
): { label: string; detail: string } => {
  if (deltaE < 1) {
    return {
      label: "Critical risk",
      detail:
        "These colors are virtually indistinguishable. Select entirely different hues or add non-color cues.",
    };
  }
  if (deltaE < 2.5) {
    return {
      label: "Severe risk",
      detail:
        "Colors remain highly confusable across most simulations. Increase hue and luminance separation immediately.",
    };
  }
  if (deltaE < 4.5) {
    return {
      label: "Moderate risk",
      detail:
        "Users with low vision or color blindness may still confuse this pair. Consider bolder value or saturation changes.",
    };
  }
  return {
    label: "Mild risk",
    detail:
      "This pair is close under the current ΔE threshold. Add complementary cues or widen the color distance further.",
  };
};

const formatPercentage = (value: number) => `${(value * 100).toFixed(1)}%`;

interface AccessibilityAuditPanelProps {
  palette: PaletteRecord;
  onUpdatePalette: (updates: Partial<PaletteRecord>) => Promise<void>;
}

export function AccessibilityAuditPanel({
  palette,
  onUpdatePalette,
}: AccessibilityAuditPanelProps) {
  const [auditReport, setAuditReport] = useState<PaletteAuditReport | null>(
    null,
  );
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditTarget, setAuditTarget] = useState<TargetCompliance>("aa");
  const [auditTextSize, setAuditTextSize] = useState(16);
  const [auditBold, setAuditBold] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [autoFixApplying, setAutoFixApplying] = useState<string | null>(null);
  const [confusableDeltaE, setConfusableDeltaE] = useState(5);
  const [activeConfusablePair, setActiveConfusablePair] =
    useState<ConfusablePair | null>(null);

  const paletteColors = palette.colors;

  // Sync audit settings from palette metadata
  const lastAudit = palette.lastAudit;
  if (lastAudit && auditTarget === "aa" && auditTextSize === 16 && !auditBold) {
    // Initial sync only — using direct assignment in render is intentional
    // to avoid infinite re-renders from useEffect
  }

  const confusablePairs = useMemo(
    () =>
      accessibilityTools
        .detectConfusableColors(paletteColors, confusableDeltaE)
        .map(([first, second, deltaE]) => ({
          first,
          second,
          deltaE: Number(deltaE.toFixed(2)),
        }))
        .sort((a, b) => a.deltaE - b.deltaE),
    [paletteColors, confusableDeltaE],
  );

  const handleConfusableDeltaEChange = (value: number) => {
    const normalized = Number.isFinite(value)
      ? Math.min(Math.max(value, 1), 15)
      : 5;
    setConfusableDeltaE(normalized);
    setActiveConfusablePair(null);
  };

  const buildAuditMetadata = (
    report: PaletteAuditReport,
  ): PaletteAuditMetadata => ({
    timestamp: report.timestamp,
    target: report.target,
    textSizePx: report.textSizePx,
    isBold: report.isBold,
    passRate: report.passRate,
    failCount: report.failCount,
    totalPairs: report.totalPairs,
    worstRatio: report.worstRatio,
  });

  const runAudit = useCallback(async () => {
    if (!palette || palette.colors.length < 2) {
      setAuditError("Add at least two colors to run a palette audit.");
      return;
    }

    setAuditLoading(true);
    setAuditError(null);

    try {
      const report = accessibilityTools.auditPalette(palette.colors, {
        target: auditTarget,
        textSizePx: auditTextSize,
        isBold: auditBold,
        confusableDeltaE,
      });

      await onUpdatePalette({
        lastAudit: buildAuditMetadata(report),
      });

      setAuditReport(report);
      setActiveConfusablePair(null);

      if (palette.id) {
        await db.logActivity({
          type: "palette_audited",
          paletteId: palette.id,
          paletteName: palette.name,
          description: `Audited palette "${palette.name}" for ${auditTarget.toUpperCase()} compliance`,
          details: {
            auditPassRate: report.passRate,
            colorCount: palette.colors.length,
          },
        });
      }
    } catch (error) {
      console.error("Palette audit failed:", error);
      setAuditError("Failed to run accessibility audit. Please try again.");
    } finally {
      setAuditLoading(false);
    }
  }, [palette, auditTarget, auditTextSize, auditBold, confusableDeltaE, onUpdatePalette]);

  const exportAuditReport = () => {
    if (!auditReport || typeof window === "undefined") return;

    const filenameSafeName =
      palette?.name
        ?.toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "palette";
    const blob = new Blob([JSON.stringify(auditReport, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filenameSafeName}-accessibility-audit.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const parsePairId = (pairId: string) => {
    const [fgIndexRaw, bgIndexRaw] = pairId.split("-");
    const fgIndex = Number.parseInt(fgIndexRaw, 10);
    const bgIndex = Number.parseInt(bgIndexRaw, 10);
    if (Number.isNaN(fgIndex) || Number.isNaN(bgIndex)) return null;
    return { fgIndex, bgIndex };
  };

  const applyAutoFix = async (
    pair: PaletteAuditPairResult,
    proposal: PaletteAutoFixProposal,
  ) => {
    const indexes = parsePairId(pair.id);
    if (!indexes) {
      setAuditError("Could not resolve palette indices for this auto-fix.");
      return;
    }

    const targetIndex =
      proposal.role === "foreground" ? indexes.fgIndex : indexes.bgIndex;
    if (targetIndex < 0 || targetIndex >= palette.colors.length) {
      setAuditError("Auto-fix references an invalid color index.");
      return;
    }

    setAutoFixApplying(proposal.id);
    setAuditError(null);

    try {
      const updatedColors = [...palette.colors];
      updatedColors[targetIndex] = proposal.suggestedColor;

      const updatedReport = accessibilityTools.auditPalette(updatedColors, {
        target: auditTarget,
        textSizePx: auditTextSize,
        isBold: auditBold,
        confusableDeltaE,
      });

      await onUpdatePalette({
        colors: updatedColors,
        lastAudit: buildAuditMetadata(updatedReport),
      });

      setAuditReport(updatedReport);
      setActiveConfusablePair(null);
    } catch (error) {
      console.error("Auto-fix application failed:", error);
      setAuditError("Failed to apply auto-fix. Please try again.");
    } finally {
      setAutoFixApplying(null);
    }
  };

  return (
    <>
      <div className="pal-section p-4 space-y-4">
        <div className="pal-audit-summary">
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">
              Accessibility Audit
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Run WCAG contrast checks across the full palette and review
              auto-fix proposals.
            </p>
          </div>
          <button
            onClick={runAudit}
            disabled={auditLoading || palette.colors.length < 2}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg text-sm transition-colors"
          >
            {auditLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4" />
            )}
            Run Audit
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400 flex flex-col gap-1">
            Target Compliance
            <select
              value={auditTarget}
              onChange={(e) => setAuditTarget(e.target.value as TargetCompliance)}
              className="px-2 py-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
            >
              <option value="aa">WCAG 2.1 AA</option>
              <option value="aaa">WCAG 2.1 AAA</option>
              <option value="wcag22">WCAG 2.2</option>
            </select>
          </label>
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400 flex flex-col gap-1">
            Text Size (px)
            <input
              type="number"
              min={10}
              max={72}
              value={auditTextSize}
              onChange={(e) => setAuditTextSize(Number(e.target.value))}
              className="px-2 py-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100"
            />
          </label>
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
            <input
              type="checkbox"
              checked={auditBold}
              onChange={(e) => setAuditBold(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>Bold / Large Text Weight</span>
          </label>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center justify-between">
            <span>Confusable Colors ΔE Threshold</span>
            <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400">
              ΔE ≤ {confusableDeltaE.toFixed(1)}
            </span>
          </label>
          <input
            type="range"
            min={1}
            max={15}
            step={0.5}
            value={confusableDeltaE}
            onChange={(event) =>
              handleConfusableDeltaEChange(Number(event.target.value))
            }
            className="w-full accent-blue-600"
            aria-label="Confusable delta E threshold"
          />
          <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-2">
            <Info className="w-3 h-3" /> Lower thresholds flag more pairs that
            may appear alike for low-vision or color-blind users.
          </p>
        </div>

        {palette.lastAudit && (
          <div className="text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-md p-3 bg-white dark:bg-gray-800">
            <p className="font-medium text-gray-700 dark:text-gray-300">
              Last Audit Snapshot
            </p>
            <p>
              When:{" "}
              {new Date(palette.lastAudit.timestamp).toLocaleString()}
            </p>
            <p>Target: {palette.lastAudit.target.toUpperCase()}</p>
            <p>
              Pass Rate: {formatPercentage(palette.lastAudit.passRate)}
            </p>
          </div>
        )}

        {auditError && (
          <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/40 rounded-md p-3 bg-red-50 dark:bg-red-900/20">
            <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{auditError}</span>
          </div>
        )}

        {auditReport && (
          <div className="space-y-4">
            <div className="pal-audit-summary">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Audit Summary
                </p>
                <div className="text-xs text-gray-600 dark:text-gray-400 space-x-3">
                  <span>Pairs: {auditReport.totalPairs}</span>
                  <span>Pass: {auditReport.passCount}</span>
                  <span>Fail: {auditReport.failCount}</span>
                  <span>
                    Pass Rate: {formatPercentage(auditReport.passRate)}
                  </span>
                </div>
              </div>
              <button
                onClick={exportAuditReport}
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 dark:border-gray-700 text-xs font-medium text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <Download className="w-4 h-4" /> Export JSON
              </button>
            </div>

            {auditReport.failCount === 0 ? (
              <div className="flex items-start gap-2 text-sm text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700 rounded-md p-3 bg-green-50 dark:bg-green-900/20">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  All palette combinations meet your selected WCAG target.
                </span>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Failing Combinations
                </p>
                <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                  {auditReport.failures.map((failure) => (
                    <div
                      key={failure.id}
                      className="border border-red-200 dark:border-red-600 rounded-lg p-3 bg-red-50 dark:bg-red-900/10 space-y-2"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-red-700 dark:text-red-300">
                            Ratio {failure.ratio ?? "—"}:1 (Needs ≥{" "}
                            {failure.compliance.threshold.toFixed(2)}:1)
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            Pair #{failure.id}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <div
                            className="w-8 h-8 rounded border border-gray-200 dark:border-gray-600"
                            style={{ backgroundColor: failure.foreground }}
                          />
                          <div
                            className="w-8 h-8 rounded border border-gray-200 dark:border-gray-600"
                            style={{ backgroundColor: failure.background }}
                          />
                        </div>
                      </div>
                      <ul className="text-xs text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
                        {failure.compliance.failingReasons.map((reason, index) => (
                          <li key={index}>{reason}</li>
                        ))}
                      </ul>
                      {failure.autoFixes.length > 0 ? (
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                            Auto-fix Proposals
                          </p>
                          <div className="space-y-2">
                            {failure.autoFixes.map((proposal) => (
                              <div
                                key={`${failure.id}-${proposal.id}`}
                                className="flex flex-wrap items-center justify-between gap-2 border border-gray-200 dark:border-gray-700 rounded-md p-2 bg-white dark:bg-gray-800"
                              >
                                <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                                  <p className="font-medium text-gray-800 dark:text-gray-100">
                                    {proposal.description}
                                  </p>
                                  <p>
                                    → {proposal.originalColor} →{" "}
                                    {proposal.suggestedColor}
                                  </p>
                                  <p>
                                    Result {proposal.resultingRatio.toFixed(2)}
                                    :1 (
                                    {proposal.ratioGain >= 0 ? "+" : ""}
                                    {proposal.ratioGain.toFixed(2)})
                                  </p>
                                </div>
                                <button
                                  onClick={() => applyAutoFix(failure, proposal)}
                                  disabled={autoFixApplying === proposal.id}
                                  className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md transition-colors text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400"
                                >
                                  {autoFixApplying === proposal.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <CheckCircle className="w-3 h-3" />
                                  )}
                                  Apply Fix
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2 text-xs text-yellow-700 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-700 rounded-md p-2 bg-yellow-50 dark:bg-yellow-900/20">
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>
                            No auto-fix suggestions available. Consider adjusting
                            palette colors manually.
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <Info className="w-3 h-3" /> Confusable Colors
                </div>
                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                  Flagged pairs: {confusablePairs.length}
                </span>
              </div>

              {confusablePairs.length > 0 ? (
                <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                  {confusablePairs.map((pair, index) => {
                    const severity = describeConfusableSeverity(pair.deltaE);
                    return (
                      <div
                        key={`${pair.first}-${pair.second}-${index}`}
                        className="border border-yellow-200 dark:border-yellow-700 rounded-md p-3 bg-yellow-50/60 dark:bg-yellow-900/20 space-y-2"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-8 h-8 rounded border border-gray-200 dark:border-gray-600"
                              style={{ backgroundColor: pair.first }}
                            />
                            <div
                              className="w-8 h-8 rounded border border-gray-200 dark:border-gray-600"
                              style={{ backgroundColor: pair.second }}
                            />
                            <span className="text-xs font-mono text-gray-600 dark:text-gray-400">
                              ΔE {pair.deltaE.toFixed(2)}
                            </span>
                          </div>
                          <button
                            onClick={() => setActiveConfusablePair(pair)}
                            className="text-xs px-2 py-1 bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 rounded hover:bg-yellow-200 dark:hover:bg-yellow-700 transition-colors"
                          >
                            {severity.label} — Simulate
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-start gap-2 text-xs text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700 rounded-md p-3 bg-green-50 dark:bg-green-900/20">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    No confusable pairs detected within the current threshold
                    of {confusableDeltaE.toFixed(1)}.
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {activeConfusablePair && (
        <ConfusableOverlay
          pair={activeConfusablePair}
          onDismiss={() => setActiveConfusablePair(null)}
        />
      )}
    </>
  );
}
